import React, { useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import Icon from 'Components/Icon';
import dayjs from 'dayjs';
import { useIntl } from 'react-intl';

const roundToNext = (dateTime, intervals) => {
  const minutes = dateTime.minute();
  const remainder = minutes % intervals;
  return remainder === 0
    ? dateTime
    : dateTime.add(intervals - remainder, 'minute').second(0);
};

const DatePicker = ({
  onChange,
  size,
  selectsRange = true,
  futureTimeOnly,
  initialValue,
  timeIntervals = 15,
  ...props
}) => {
  const { formatMessage } = useIntl();
  const [dateRange, setDateRange] = useState(
    selectsRange && initialValue?.[0] && initialValue?.[1]
      ? initialValue.map((v) => {
          const d = dayjs(v);
          return d.isValid() ? d.toDate() : null;
        })
      : [null, null]
  );
  const [date, setDate] = useState(
    initialValue && dayjs(initialValue).isValid()
      ? dayjs(initialValue).toDate()
      : null
  );
  const [startDate, endDate] = dateRange;
  const selected = selectsRange ? startDate : date;
  return (
    <ReactDatePicker
      wrapperClassName={size === 'large' ? 'large' : ''}
      selected={selected}
      placeholderText={formatMessage({
        id: 'date-picker-field-placeholder',
        defaultMessage: 'Select date',
        description: `Placeholder for 'Select date' in Date picker field`,
      })}
      onChange={(update) => {
        if (selectsRange) {
          setDateRange(update);
          if (update[0] && update[1]) {
            onChange && onChange(update);
          }
        } else {
          let toUpdate = update;
          if (futureTimeOnly && dayjs(toUpdate).isBefore(dayjs())) {
            toUpdate = new Date();
          }
          const rounded = roundToNext(dayjs(toUpdate), timeIntervals);
          if (rounded.isValid()) {
            setDate(rounded.toDate());
            onChange && onChange(rounded.toDate());
          }
        }
      }}
      startDate={selectsRange ? startDate : null}
      endDate={selectsRange ? endDate : null}
      showIcon
      icon={
        selected ? (
          <button
            type="button"
            onClick={() => {
              setDate(null);
              setDateRange([null, null]);
              onChange && onChange(selectsRange ? [null, null] : null);
            }}
          >
            <Icon name="close" />
          </button>
        ) : (
          <div className="pointer-none">
            <Icon name="calendar_today" />
          </div>
        )
      }
      selectsRange={selectsRange}
      monthsShown={2}
      popperPlacement="bottom-end"
      preventOpenOnFocus={true}
      dateFormat={'d MMM yyyy'}
      autoComplete="off"
      customInput={
        <input
          className={`ant-input ${size === 'large' ? 'ant-input-lg' : ''}`}
        />
      }
      minTime={
        futureTimeOnly &&
        date &&
        dayjs(date).isValid() &&
        dayjs(date).isSame(dayjs(), 'day')
          ? new Date()
          : null
      }
      maxTime={
        futureTimeOnly &&
        date &&
        dayjs(date).isValid() &&
        dayjs(date).isSame(dayjs(), 'day')
          ? dayjs().hour(23).minute(59).toDate()
          : null
      }
      timeIntervals={timeIntervals}
      {...props}
    />
  );
};

export default DatePicker;
